<template>
  <v-layout column>
    <v-flex class="pb-16">
      <text-field
        v-model.trim="form.name"
        autofocus
        :validator="vs.form.name"
        :label="adminText('workflowsAll.workshop_name_5397223', 'Workshop name')"
      />
    </v-flex>
    <v-layout class="pb-16">
      <v-flex class="pr-30" xs6 md12>
        <datetime-field
          v-model="form.from"
          :min="event.from.toString()"
          :max="form.to ? form.to.toString() : event.to.toString()"
          :error-messages="{
            minDate: adminText('workflowsAll.start_date_must_be_after_the_start_of_the_eve_759b82f', 'Start date must be after the start of the event'),
            maxDate: adminText('workflowsAll.start_date_should_be_before_the_end_of_the_ev_806a045', 'Start date should be before the end of the event or workshop'),
          }"
          :validator="vs.form.from"
          :label="adminText('workflowsAll.starts_fc612a2', 'Starts')"
        />
      </v-flex>
      <v-flex xs6 md12>
        <datetime-field
          v-model="form.to"
          :min="form.from ? form.from.toString() : event.from.toString()"
          :max="event.to.toString()"
          :error-messages="{
            minDate: adminText('workflowsAll.end_date_must_be_after_the_start_of_the_event_219ed61', 'End date must be after the start of the event or workshop'),
            maxDate: adminText('workflowsAll.end_date_must_be_before_the_end_of_the_event_97e993e', 'End date must be before the end of the event'),
          }"
          :validator="vs.form.to"
          :label="adminText('workflowsAll.ends_91856dc', 'Ends')"
        />
      </v-flex>
    </v-layout>
    <v-flex class="pb-16">
      <autocomplete-field
        v-model="form.lecturers"
        :items="workflowUi(suggestedLecturers)"
        :on-search-change="populateSuggested"
        :loading="suggestionsLoading"
        :filter="lecturersFilter"
        multiple
        :label="adminText('workflowsAll.lecturers_34e6562', 'Lecturers')"
        item-render="name"
        return-object
      >
        <template v-slot:selection="{ item }">
          <v-chip small @click="removeAttendee(item.id)">
            {{ workflowLabel(item.fields['First name']) }} {{ workflowLabel(item.fields['Last name']) }}
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <v-list-tile-content>
            {{ workflowLabel(item.fields['First name']) }} {{ workflowLabel(item.fields['Last name']) }}
          </v-list-tile-content>
        </template>
        <template v-slot:no-data>
          <span style="margin-left: 10px">{{ adminText('workflowsAll.start_typing_attendee_name_8b03f2c', 'Start typing attendee name...') }}</span>
        </template>
      </autocomplete-field>
    </v-flex>
    <v-flex v-if="event.extraModules.conferenceMobileApp" class="pb-16">
      <autocomplete-field
        v-model="form.hallId"
        :items="workflowUi(suggestedHalls)"
        :loading="suggestionsLoadingHalls"
        :filter="hallsFilter"
        :validator="vs.form.hallId"
        :label="adminText('workflowsAll.hall_a3b092b', 'Hall')"
        item-render="name"
        item-select="id"
      >
        <template v-slot:selection="{ item }">
          <v-chip small>
            {{ workflowLabel(item['name']) }}
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <v-list-tile-content>{{ workflowLabel(item['name']) }}</v-list-tile-content>
        </template>
        <template v-slot:no-data>
          <span>{{ adminText('workflowsAll.no_data_d802d23', 'No data') }}</span>
        </template>
      </autocomplete-field>
    </v-flex>
    <v-flex>
      <text-field
        v-model.number="form.attendanceLimit"
        :disabled="form.unlimitedAttendance"
        :validator="vs.form.attendanceLimit"
        :label="adminText('workflowsAll.attendance_limit_25aa5e1', 'Attendance limit')"
        mask="number"
      />
    </v-flex>
    <v-flex class="pb-16">
      <switch-field
        v-model="form.unlimitedAttendance"
        :validator="vs.form.unlimitedAttendance"
        :label="adminText('workflowsAll.workshops_s_attendance_is_unlimited_ef10178', 'Workshops\'s attendance is unlimited')"
      />
    </v-flex>
    <!--
      COR-1684: only conference events show this — there is no other app to be asked in.
      The link appears once there is something behind it; the create form never has.
    -->
    <v-flex v-if="event.extraModules && event.extraModules.conferenceMobileApp" class="pb-16">
      <switch-field
        v-model="form.feedbackEnabled"
        :label="adminText('workflowsAll.get_feedback_from_the_conference_app_2ed7548', 'Get feedback from the conference app')"
      />
      <a v-if="feedbackTotal" class="feedback-link" @click="$emit('showFeedback')">
        {{ adminText('workflowsAll.see_all_the_feedback_2c5dd2a', 'See all the feedback') }}
        <span class="feedback-count">({{ workflowLabel(feedbackTotal) }})</span>
      </a>
    </v-flex>
    <v-flex class="pb-16">
      <textarea-field v-model.trim="form.description" :validator="vs.form.description" />
    </v-flex>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { apolloClient } from '../../apollo'
import { SEARCH_EVENT_ACCREDITATIONS } from '../../graphql/event.graphql'
import { SEARCH_HALLS } from '../../graphql/halls.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'workshop-form',
  props: {
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    halls: {
      type: Array,
      default: () => [],
    },
    vs: {
      type: Object,
      default: () => ({}),
    },
    // COR-1684: how many pieces of feedback this workshop has. 0 on the create form.
    feedbackTotal: {
      type: Number,
      default: 0,
    },
  },
  data() {
    const { lecturers, hall, ...rest } = this.initialValues

    const initialLecturers = (lecturers || []).map(this.suggestedMapper)

    const form = {
      // Declared so Vue 2 can observe it: on the create form nothing else supplies the
      // key, and a switch writing a brand new property would not reach the parent.
      feedbackEnabled: false,
      ...rest,
      lecturers: initialLecturers,
      hallId: hall && hall.id,
    }

    return {
      form,
      suggestedLecturers: initialLecturers,
      suggestedHalls: this.halls,
      lecturersSearch: '',
      suggestionsLoading: false,
      suggestionsLoadingHalls: false,
      requestInProgress: undefined,
    }
  },
  watch: {
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
  },
  methods: {
    suggestedMapper(attendee) {
      return {
        name: `${attendee.fields['First name']} ${attendee.fields['Last name']}`,
        ...attendee,
      }
    },
    lecturersFilter({ category, fields }, input) {
      input = input.toLowerCase()
      const fieldsMatch = Object.values(fields).some(fieldValue =>
        fieldValue.toLowerCase().includes(input),
      )
      const categoryMatch = category ? category.name.toLowerCase().includes(input) : false

      return fieldsMatch || categoryMatch
    },
    async populateSuggested(input) {
      if (!input || !input.trim()) return

      if (this.requestInProgress) clearTimeout(this.requestInProgress)
      this.suggestionsLoading = true
      this.requestInProgress = setTimeout(async () => {
        const { data, errors, loading } = await apolloClient.query({
          query: SEARCH_EVENT_ACCREDITATIONS,
          variables: {
            eventId: this.event.id,
            status: 'ALLOWED',
            input,
          },
        })
        if (!errors && !loading) {
          const { searchEventAccreditations } = data
          this.suggestedLecturers = searchEventAccreditations.map(this.suggestedMapper)
        }
        this.suggestionsLoading = false
      }, 500)
    },
    hallsFilter({ name }, input) {
      input = input.toLowerCase()
      const nameMatch = Object.values(name).some(fieldValue =>
        fieldValue.toLowerCase().includes(input),
      )

      return nameMatch
    },
    removeAttendee(id) {
      console.log('Clicked!')
      this.form.lecturers = this.form.lecturers.filter(l => l.id !== id)
    },
  },
}
</script>

<style scoped>
/* Indented to the switch's label rather than the switch, as in the design. */
.feedback-link {
  display: inline-block;
  margin-left: 52px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.2;
  color: #0277bd;
  text-decoration: underline;
  cursor: pointer;
}

.feedback-count {
  color: #979797;
  text-decoration: none;
  margin-left: 8px;
}
</style>
